import _get from 'lodash/get';
import { createModel, post, isPresent } from '@/lib/utils';
import store from '@/store';
import router from '@/router';

const setCachedModel = (className, { model }) => {
  store.dispatch('editObject/setCachedModel', { model, className });
};

const handleRouteCaching = ({ className, routeName, id }) => {
  if (!store.getters['cached/canCacheClass'](className)) return; // Exit early if not an allowed class to cache.

  store.dispatch('cached/cacheScreenData', {
    model: store.getters['editObject/record'],
    routeName,
    id,
    className,
  });
};

/**
 * Edit Screen middleware
 * @param {Object} to
 * @param {Object} from
 * @param {Function} next
 */
const editScreenParamHandler = async (to, from, next) => {
  const { params, query } = to;
  const className = _get(to, 'meta.classname') || _get(to, 'params.classname');
  // Handle retrieving a cached model
  if (params.isCached) {
    const isRecordDirty = store.getters['cached/previousScreenData'][className].isDirty;
    setCachedModel(className, params);
    handleRouteCaching({ className, routeName: to.name, id: params.id }); // Reset cache to prevent loss of object reference (e.g. on refresh)
    store.dispatch('editObject/setRecordIsDirty', isRecordDirty); // Handle hydrating previous dirty tracking state for cached models
    next();
    return;
  }

  const isNewScreen = from.name && from.name.endsWith('new_screen');
  const isSameClassName = from.meta?.classname === to.meta?.classname;
  const isNotDirty = !store.getters['editObject/isRecordDirty'];
  // Get id from order id used on new screen to match with id used on edit screen.
  // When routing from new_order_screen to order_edit_screen when saving a
  // new order the classnames are different so need to use mainorderid to match instead of classname
  let mainOrderId;
  const record = store.getters['editObject/record'];
  // Coming from order_new_screen with !_isAdded means the order was successfully saved
  if (from.name === 'order_new_screen' && !record._isAdded) {
    mainOrderId = record.mainorderid;
  }

  // Handle setting model on saving a new record via router naming conventions.
  if (isNewScreen && (isSameClassName || mainOrderId === to.params?.id) && isNotDirty) {
    await store.dispatch('editObject/setClassName', className);
    handleRouteCaching({ className, routeName: to.name, id: params.id });
    next();
    return;
  }

  const keys = store.getters['meta/primary_keys'](className);
  keys.pop(); // Pop off last key in list as last key serves as slug which doesn't need checked for presence.

  const queryKeys = Object.keys(query);
  const missingKeys = keys.filter(key => !queryKeys.includes(key));

  // Set model, cache, and initialize lazyloads prior to calling next, otherwise missing URL parameters.
  if (missingKeys.length === 0) {
    await store.dispatch('editObject/setClassName', className);
    await store.dispatch('editObject/get', { params, query, className });
    if (!store.getters['editObject/record']) {
      next('/');
      return;
    }
    handleRouteCaching({ className, routeName: to.name, id: params.id });
    next();
    return;
  }

  const classLabel = store.getters['meta/class_label'](className, true);

  let text = `Missing URL parameters for ${classLabel}: `;
  missingKeys.forEach(key => {
    text += `${key}, `;
  });
  text = text.slice(0, text.length - 2);

  store.dispatch('notify/push', {
    title: 'Incomplete URL Parameters',
    text,
  });

  next('/');
};

/**
 * New Screen middleware
 * @param {Object} to
 * @param {Object} from
 * @param {Function} next
 */
const newScreenParamHandler = async (to, from, next) => {
  let defaults = {};
  const className = _get(to, 'meta.classname') || _get(to, 'params.classname');
  if (to.params.isCached) {
    const isRecordDirty = store.getters['cached/previousScreenData'][className].isDirty;
    setCachedModel(className, to.params);
    handleRouteCaching({ className, routeName: to.name, id: to.params.id }); // Reset cache to prevent loss of object reference (e.g. on refresh)
    store.dispatch('editObject/setRecordIsDirty', isRecordDirty); // Handle hydrating previous dirty tracking state for cached models
    next();
    return to.params.model;
  }

  // Handling of Sequeced PK
  let newPk = {};
  const sequencedKeys = store.getters['meta/sequenced_keys'](className);
  if (sequencedKeys.length) {
    // the framework currently only supports at most one sequenced key per table
    // it is safe to currently destructure out the first item of the array.
    const [sequencedPk] = sequencedKeys;
    const url = `/tg/${className.toLowerCase()}/get_next${sequencedPk}`;
    const { results } = await post(url);
    newPk = { ...results[0] };
  }

  // Handle copying server defaults
  if (to.meta.getDefaults) {
    const defaultsPath = to.meta.getDefaults;
    const siteid = store.getters['auth/loggedInSiteId'];
    const params = { siteid };
    const { results } = await post(`/tg/${className.toLowerCase()}/${defaultsPath}`, params);
    defaults = { ...results[0] };
  }

  // copyRecordConfig.copyData is a copy made by the server
  // copyRecordConfig.copiedFields is a copy made by the client
  const copyRecordConfig = store.getters['editObject/copyRecordConfig'];
  if (copyRecordConfig) defaults = { ...defaults, ..._get(copyRecordConfig, 'copyData', {}) };

  let model = {};

  // Handle case of a new CustomerOrder, otherwise perform usual model initialization.
  if (className === 'CustomerOrder') {
    model = createModel('CustomerOrder');
    if (_get(copyRecordConfig, 'copyData')) {
      model = { ...model, ...copyRecordConfig.copyData };
    } else {
      let newOrder = createModel('Order');
      const { results } = await store.dispatch('editObject/post', {
        url: '/tg/order/get_nextorderid',
        params: {
          ordersiteid: store.getters['auth/loggedInSiteId'],
        },
      });
      const orderDefaults = results[0];
      model.newmainorderid = orderDefaults.neworderid;
      if (store.getters['meta/systemParameter']('default_salesperson1') === 'Y') {
        newOrder.salesperson1alias = store.getters['auth/useralias'];
      }
      newOrder = { ...newOrder, ...orderDefaults };
      model.orders[0] = newOrder;
    }
  } else {
    model = createModel(className, { ...newPk, ...defaults });
  }

  await store.dispatch('editObject/setClassName', className);
  await store.dispatch('editObject/setModel', { model });
  if (_get(copyRecordConfig, 'copiedFields')) await store.dispatch('editObject/updateMultipleValues', { keys: copyRecordConfig.copiedFields, path: 'record' });
  store.dispatch('editObject/setCopyRecordConfig', null);
  handleRouteCaching({ className, routeName: to.name, id: to.params.id });

  next();
  return model; // return only necessary for tests, as next stops execution in router context
};

const determineParamHandler = async (to, from, next) => {
  let toRoute;
  const routingToOrderScreen = to.name === 'order_edit_screen' || to.name === 'order_new_screen';
  if (routingToOrderScreen) {
    const meta = { ...to.meta, classname: 'CustomerOrder' }; // OrderScreen is a special case as it's underlying class is CustomerOrder
    toRoute = { ...to, meta };
  } else toRoute = to;

  if (to.name && to.name.endsWith('_new_screen')) await newScreenParamHandler(toRoute, from, next);
  else await editScreenParamHandler(toRoute, from, next);

  return to.name; // return only necessary for tests, as next is called by above param handlers
};

/**
 * Common logic to notify the user of the missing permission and reject the route
 * @param {Object} from
 * @param {Function} next
 * @returns {boolean} missingPermission - The missing permission name
 */
const rejectRouteHandler = (from, next, missingPermission, message) => {
  store.dispatch('notify/push', {
    title: 'Missing Permission',
    text: message || `You do not have the '${missingPermission}' permission`,
  });
  if (!from.name) next({ path: '/' });
  else next(false);
};

/**
 * Navigates to edit route on successful record save.
 */
const navigateToEdit = (model, isDynaform) => {
  const className = model._classname;
  const pk = {};
  const queryParamKeys = {};

  const keys = store.getters['meta/primary_keys'](className);
  pk.id = model[keys.pop()];
  if (keys.length > 0) {
    for (const key of keys) queryParamKeys[key] = model[key];
  }

  // Dynamically determine the route name based on the class name when navigating to the edit screen
  const classRoute = className.match(/[A-Z][a-z]+/g).map(name => name.toLowerCase());
  let routeName = `${classRoute.join('_')}_edit_screen`;

  // If the class name is a dynaform, we need to cascade to edit_screen, and order screen is a special case due to order rollup
  if (isDynaform) routeName = 'edit_screen';
  if (className === 'CustomerOrder') routeName = 'order_edit_screen';
  if (router.currentRoute.value.name === 'salesperson_budget_period_new_screen') {
    routeName = 'salesperson_budget_period_edit_screen';
  } else if (router.currentRoute.value.name === 'site_daily_budget_new_screen') {
    routeName = 'site_daily_budget_edit_screen';
  }

  store.dispatch('cached/removeDataProvider', className); // Invalidate data provider cache as a new record was created

  router.push({ name: routeName, params: pk, query: queryParamKeys });
};

const updateStateOnSave = (response) => {
  const results = response.results;
  const hasErrors = response.hasErrors;
  store.dispatch('editObject/updateRecord', results);
  if (!hasErrors) store.dispatch('editObject/setRecordIsDirty', false); // Successful save, reset dirty tracking
  return !hasErrors; // Return false when errors are indicated so caller that the record was not saved
};

/**
 * Combines save action with state updating function.
 */
const performSave = async (className) => {
  const response = await store.dispatch('editObject/save', className);
  updateStateOnSave(response);
  store.dispatch('cached/updateCachedScreenModel', { model: response.results, className }); // Update model cache with save results
  return response;
};

/**
 * Saves model mapped from editObject.record based on _classname's primary keys,
 * then if new screen, push to corresponding edit screen.
 */
const defaultSave = async (payload = {}) => {
  const { model: mdl, isDynaform, newRecord = null } = payload;

  // Determine model, className, and whether or not it's a new record
  const model = mdl || store.getters['editObject/record'];
  const className = model._classname;
  // newRecord is an override for when just 'original' will never be present (e.g. TruckRoute)
  const isNewRecord = isPresent(newRecord) ? newRecord : !isPresent(model.original);

  const response = await performSave(className); // Perform default save logic and state updating

  if (response.hasErrors) return false; // Do not navigate as errors are present (stated updated to reflect).
  if (isNewRecord) navigateToEdit(response.results, isDynaform); // Prepare then push to edit screen if new record.

  return true; // Indicate a successful save (for purposes of emitting a record-saved event)
};

export {
  editScreenParamHandler,
  newScreenParamHandler,
  determineParamHandler,
  rejectRouteHandler,
  navigateToEdit,
  defaultSave,
  performSave,
  updateStateOnSave,
};
